import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, NavLink, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { api } from '../context/AuthContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';

// ── Theme: matches main login page (dark slate + orange accent) ────────────────
const S = {
  card: {
    background: '#ffffff',
    borderRadius: '16px', padding: '1.5rem',
    border: '1px solid #f1f5f9',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  statCard: {
    background: '#f8fafc', borderRadius: '12px',
    padding: '1rem 1.25rem', border: '1px solid #e2e8f0',
  },
  badge: (color) => ({
    display: 'inline-block', padding: '3px 10px', borderRadius: '20px',
    fontSize: '11px', fontWeight: '600',
    ...(color === 'green'  && { background: '#dcfce7', color: '#166534' }),
    ...(color === 'yellow' && { background: '#fef9c3', color: '#854d0e' }),
    ...(color === 'red'    && { background: '#fee2e2', color: '#991b1b' }),
    ...(color === 'gray'   && { background: '#f1f5f9', color: '#475569' }),
    ...(color === 'blue'   && { background: '#ffedd5', color: '#9a3412' }),
  }),
  btn: (v = 'primary') => ({
    padding: '8px 16px', borderRadius: '10px', border: 'none', cursor: 'pointer',
    fontSize: '13px', fontWeight: '600', fontFamily: "'Sora', sans-serif",
    ...(v === 'primary' && { background: 'linear-gradient(135deg,#F97316,#EF4444)', color: '#fff' }),
    ...(v === 'outline' && { background: '#fff', color: '#374151', border: '1px solid #e2e8f0' }),
    ...(v === 'success' && { background: '#dcfce7', color: '#166534', border: 'none' }),
    ...(v === 'danger'  && { background: '#fee2e2', color: '#991b1b', border: '1px solid rgba(248,113,113,0.2)' }),
  }),
  inp: {
    width: '100%', padding: '11px 14px', borderRadius: '10px', boxSizing: 'border-box',
    border: '1px solid rgba(255,255,255,0.1)', fontSize: '14px',
    fontFamily: "'Sora', sans-serif", outline: 'none',
    color: '#1e293b', background: '#fff', marginBottom: '12px',
  },
  pageWrap: {
    minHeight: '100vh',
    background: '#f8fafc',
    fontFamily: "'Sora', sans-serif",
  },
  th: {
    padding: '8px 12px', textAlign: 'left', color: '#64748b',
    fontWeight: '600', fontSize: '11px', textTransform: 'uppercase',
    borderBottom: '1px solid #f1f5f9',
  },
  td: { padding: '12px', color: '#374151', fontSize: '13px' },
};

const fmt     = (n) => `₹${parseFloat(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

// ─── LAYOUT ───────────────────────────────────────────────────────────────────
function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    api.get('/notifications').then(r => {
      setUnread(r.data.notifications?.filter(n => !n.is_read).length || 0);
    }).catch(() => {});
  }, []);

  const navItems = [
    { to: '/super-admin',                label: '🏛️  Overview',   end: true },
    { to: '/super-admin/admins',         label: '🏪  Lenders'             },
    { to: '/super-admin/customers',      label: '👥  Borrowers'            },
    { to: '/super-admin/analytics',      label: '📊  Analytics'            },
    { to: '/super-admin/reports',        label: '📈  Reports'              },
    { to: '/super-admin/notifications',  label: `🔔  Alerts${unread > 0 ? ` (${unread})` : ''}` },
    { to: '/super-admin/profile',        label: '👤  My Profile'           },
  ];

  return (
    <div style={{ display: 'flex', minHeight: '100vh', ...S.pageWrap }}>
      {/* Sidebar */}
      <div style={{
        width: '230px', display: 'flex', flexDirection: 'column', padding: '1.5rem 1rem',
        position: 'fixed', height: '100vh', zIndex: 10,
        background: '#0f172a',
        borderRight: '1px solid rgba(255,255,255,0.07)',
      }}>
        {/* Logo */}
        <div style={{ marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px', height: '40px', borderRadius: '12px',
            background: 'linear-gradient(135deg,#F97316,#EF4444)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '20px', boxShadow: '0 0 20px rgba(249,115,22,0.3)',
          }}>₹</div>
          <div>
            <div style={{ color: '#f1f5f9', fontWeight: '800', fontSize: '17px' }}>Udhari</div>
            <div style={{ color: '#F97316', fontSize: '11px', fontWeight: '600' }}>Super Admin</div>
          </div>
        </div>

        {/* User chip */}
        <div style={{
          background: 'rgba(249,115,22,0.1)', borderRadius: '12px', padding: '12px',
          marginBottom: '1.5rem', border: '1px solid rgba(249,115,22,0.2)',
        }}>
          <div style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '13px' }}>{user?.name}</div>
          <div style={{ color: '#F97316', fontSize: '11px', marginTop: '2px' }}>Platform Owner</div>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1 }}>
          {navItems.map(item => (
            <NavLink key={item.to} to={item.to} end={item.end} style={({ isActive }) => ({
              display: 'block', padding: '10px 14px', borderRadius: '10px',
              color: isActive ? '#F97316' : '#94a3b8', textDecoration: 'none',
              fontSize: '13px', fontWeight: isActive ? '700' : '500', marginBottom: '4px',
              background: isActive ? 'rgba(249,115,22,0.12)' : 'transparent',
              borderLeft: isActive ? '3px solid #F97316' : '3px solid transparent',
              transition: 'all 0.15s',
            })}>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <button
          onClick={() => { logout(); navigate('/login'); }}
          style={{ ...S.btn('outline'), width: '100%', marginTop: '1rem' }}
        >
          🚪 Logout
        </button>
      </div>

      {/* Main content */}
      <div style={{ marginLeft: '230px', flex: 1, padding: '2rem', minHeight: '100vh', background: '#f8fafc' }}>
        {children}
      </div>
    </div>
  );
}

// ── shared heading style ──────────────────────────────────────────────────────
const PageTitle = ({ children }) => (
  <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', margin: '0 0 2rem' }}>{children}</h1>
);

// ─── OVERVIEW ─────────────────────────────────────────────────────────────────
function Overview() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/super-admin/dashboard').then(r => setData(r.data.dashboard)).catch(() => toast.error('Failed to load'));
  }, []);

  if (!data) return <div style={{ color: '#64748b', padding: '2rem' }}>Loading...</div>;
  const { admins, customers, loans, defaulters, trust_health } = data;

  return (
    <div>
      <PageTitle>Platform Overview</PageTitle>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
        {[
          { label: 'Total Lenders',     value: admins.total,                 sub: `${admins.approved} approved · ${admins.pending} pending`, icon: '🏪' },
          { label: 'Active Borrowers',  value: customers.total,              sub: 'Platform wide',                                           icon: '👥' },
          { label: 'Total Loans',       value: loans.total,                  sub: `${loans.active} active · ${loans.overdue} overdue`,       icon: '💰', alert: parseInt(loans.overdue) > 0 },
          { label: 'Total Disbursed',   value: fmt(loans.total_disbursed),   sub: 'All time',                                                icon: '📤' },
          { label: 'Total Outstanding', value: fmt(loans.total_outstanding), sub: 'Across all lenders',                                     icon: '🕐' },
          { label: 'Total Defaulters',  value: defaulters.total,             sub: 'Unique borrowers',                                        icon: '⛔', alert: parseInt(defaulters.total) > 0 },
        ].map((s, i) => (
          <div key={i} style={{
            ...S.statCard,
            borderColor: s.alert ? 'rgba(248,113,113,0.3)' : 'rgba(255,255,255,0.08)',
            background:   s.alert ? 'rgba(220,38,38,0.08)' : 'rgba(255,255,255,0.04)',
          }}>
            <div style={{ fontSize: '26px', marginBottom: '6px' }}>{s.icon}</div>
            <div style={{ fontSize: '26px', fontWeight: '800', color: s.alert ? '#dc2626' : '#0f172a' }}>{s.value}</div>
            <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>{s.label}</div>
            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>{s.sub}</div>
          </div>
        ))}
      </div>

      <div style={S.card}>
        <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b', margin: '0 0 1rem' }}>Platform Trust Health</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
          {[
            { label: '🟢 Trustworthy', value: trust_health.trustworthy, color: '#4ade80', bg: 'rgba(74,222,128,0.08)' },
            { label: '🟡 Moderate',    value: trust_health.moderate,    color: '#fbbf24', bg: 'rgba(251,191,36,0.08)' },
            { label: '🔴 High Risk',   value: trust_health.high_risk,   color: '#f87171', bg: 'rgba(248,113,113,0.08)' },
            { label: '⛔ Blacklist',   value: trust_health.do_not_lend, color: '#c084fc', bg: 'rgba(192,132,252,0.08)' },
          ].map((t, i) => (
            <div key={i} style={{ background: t.bg, borderRadius: '12px', padding: '16px', textAlign: 'center', border: `1px solid ${t.color}22` }}>
              <div style={{ fontSize: '28px', fontWeight: '800', color: t.color }}>{t.value || 0}</div>
              <div style={{ fontSize: '12px', color: t.color, fontWeight: '600', marginTop: '4px' }}>{t.label}</div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: '1rem', padding: '12px', background: 'rgba(255,255,255,0.04)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.06)' }}>
          <span style={{ fontSize: '13px', color: '#64748b' }}>Platform Avg Trust Score: </span>
          <strong style={{ color: '#F97316' }}>{parseFloat(trust_health.avg_score || 0).toFixed(1)}</strong>
        </div>
      </div>
    </div>
  );
}

// ─── ADMINS ───────────────────────────────────────────────────────────────────
function Admins() {
  const [admins, setAdmins]           = useState([]);
  const [filter, setFilter]           = useState('PENDING');
  const [loading, setLoading]         = useState(true);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [kycModal, setKycModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/super-admin/admins?status=${filter}`);
      setAdmins(r.data.admins || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (id, action, reason) => {
    try {
      await api.put(`/super-admin/admins/${id}/approve`, { action, rejection_reason: reason });
      toast.success(`Lender ${action.toLowerCase()}`);
      setRejectModal(null);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleSuspend = async (id, currentStatus) => {
    const newStatus = currentStatus === 'SUSPENDED' ? 'APPROVED' : 'SUSPENDED';
    const label = newStatus === 'SUSPENDED' ? 'Suspend' : 'Reinstate';
    if (!window.confirm(`${label} this lender?`)) return;
    try {
      await api.put(`/super-admin/admins/${id}/suspend`, { action: newStatus });
      toast.success(`Lender ${label.toLowerCase()}d`);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  return (
    <div>
      <PageTitle>Lender Management</PageTitle>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '1.5rem' }}>
        {['PENDING', 'APPROVED', 'REJECTED', 'SUSPENDED'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            style={{ ...S.btn(filter === f ? 'primary' : 'outline'), fontSize: '12px' }}>
            {f}
          </button>
        ))}
      </div>

      {loading ? <div style={{ color: '#64748b' }}>Loading...</div> : (
        <div style={S.card}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr>
                {['Shop / Owner', 'Category', 'City', 'Phone', 'GSTIN', 'Customers', 'Total Lent', 'Status', 'Actions'].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {admins.length === 0 ? (
                <tr><td colSpan={9} style={{ padding: '2rem', textAlign: 'center', color: '#475569' }}>No lenders with status: {filter}</td></tr>
              ) : admins.map(a => (
                <tr key={a.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <td style={S.td}>
                    <div style={{ fontWeight: '700', color: '#1e293b' }}>{a.shop_name || '—'}</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>{a.full_name || a.name || '—'}</div>
                  </td>
                  <td style={S.td}><span style={S.badge('blue')}>{a.shop_category}</span></td>
                  <td style={S.td}>{a.city}</td>
                  <td style={S.td}>{a.phone}</td>
                  <td style={S.td}>
                    {a.gstin
                      ? <span style={{ fontSize: '11px', fontFamily: 'monospace', background: 'rgba(74,222,128,0.1)', color: '#4ade80', padding: '2px 6px', borderRadius: '4px' }}>{a.gstin}</span>
                      : <span style={{ color: '#475569', fontSize: '11px' }}>—</span>}
                  </td>
                  <td style={{ ...S.td, textAlign: 'center' }}>{a.customer_count || 0}</td>
                  <td style={{ ...S.td, fontWeight: '700', color: '#F97316' }}>{fmt(a.total_lent)}</td>
                  <td style={S.td}>
                    <span style={S.badge(a.status === 'APPROVED' ? 'green' : a.status === 'PENDING' ? 'yellow' : 'red')}>{a.status}</span>
                  </td>
                  <td style={S.td}>
                    {a.status === 'PENDING' && (
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        <button onClick={() => setKycModal(a)} style={{ ...S.btn('outline'), fontSize: '11px', padding: '4px 8px' }}>👁 View KYC</button>
                        <button onClick={() => handleAction(a.id, 'APPROVED')} style={S.btn('success')}>✓ Approve</button>
                        <button onClick={() => setRejectModal(a)} style={S.btn('danger')}>✗ Reject</button>
                      </div>
                    )}
                    {a.status === 'APPROVED' && (
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <span style={{ color: '#475569', fontSize: '11px' }}>Since {fmtDate(a.approved_at)}</span>
                        <button onClick={() => handleSuspend(a.id, a.status)} style={{ ...S.btn('danger'), fontSize: '11px', padding: '4px 8px' }}>⛔ Suspend</button>
                      </div>
                    )}
                    {a.status === 'SUSPENDED' && (
                      <button onClick={() => handleSuspend(a.id, a.status)} style={{ ...S.btn('success'), fontSize: '11px', padding: '4px 8px' }}>✓ Reinstate</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {kycModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '1.5rem', width: '100%', maxWidth: '600px', maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, color: '#0f172a' }}>KYC Documents — {kycModal.shop_name}</h3>
              <button onClick={() => setKycModal(null)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#94a3b8' }}>✕</button>
            </div>
            <div style={{ marginBottom: '12px', padding: '12px', background: '#f8fafc', borderRadius: '8px', fontSize: '13px' }}>
              <div><strong>Owner:</strong> {kycModal.full_name}</div>
              <div><strong>Phone:</strong> {kycModal.phone}</div>
              <div><strong>Aadhaar:</strong> {kycModal.aadhaar_number || '-'}</div>
              <div><strong>PAN:</strong> {kycModal.pan_number || '-'}</div>
              <div><strong>Shop Reg No:</strong> {kycModal.shop_registration_number || '-'}</div>
              <div><strong>GSTIN:</strong> {kycModal.gstin || '-'}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {[
                { label: 'Aadhaar Front', file: kycModal.kyc_aadhaar_front },
                { label: 'Aadhaar Back', file: kycModal.kyc_aadhaar_back },
                { label: 'PAN Card', file: kycModal.kyc_pan_card },
                { label: 'Shop Document', file: kycModal.shop_doc },
              ].map(({ label, file }) => (
                <div key={label} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' }}>{label}</div>
                  {file ? (
                    <a href={`${process.env.REACT_APP_API_URL || 'https://api-udhari.arena19.in/api'}/kyc/${file}?token=${localStorage.getItem('udhari_token')}`} target="_blank" rel="noreferrer">
                      <img
                        src={`${process.env.REACT_APP_API_URL || 'https://api-udhari.arena19.in/api'}/kyc/${file}?token=${localStorage.getItem('udhari_token')}`}
                        alt={label}
                        style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #e2e8f0', cursor: 'pointer' }}
                        onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }}
                      />
                      <div style={{ display: 'none', height: '120px', border: '1px dashed #cbd5e1', borderRadius: '8px', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '12px' }}>Not uploaded</div>
                    </a>
                  ) : (
                    <div style={{ height: '120px', border: '1px dashed #cbd5e1', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '12px' }}>Not uploaded</div>
                  )}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '1rem' }}>
              <button onClick={() => { handleAction(kycModal.id, 'APPROVED'); setKycModal(null); }} style={{ ...S.btn('success'), flex: 1 }}>✓ Approve Lender</button>
              <button onClick={() => { setRejectModal(kycModal); setKycModal(null); }} style={{ ...S.btn('danger'), flex: 1 }}>✗ Reject Lender</button>
              <button onClick={() => setKycModal(null)} style={{ ...S.btn('outline'), flex: 0.5 }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {rejectModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ ...S.card, width: '400px' }}>
            <h3 style={{ margin: '0 0 1rem', color: '#f1f5f9' }}>Reject: {rejectModal.shop_name}</h3>
            <textarea
              value={rejectReason} onChange={e => setRejectReason(e.target.value)}
              placeholder="Reason for rejection..."
              style={{ ...S.inp, height: '80px', resize: 'none', marginBottom: '12px' }}
            />
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={() => setRejectModal(null)} style={S.btn('outline')}>Cancel</button>
              <button onClick={() => handleAction(rejectModal.id, 'REJECTED', rejectReason)} style={S.btn('danger')}>Reject Lender</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── ALL CUSTOMERS ────────────────────────────────────────────────────────────
function AllCustomers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    api.get('/super-admin/customers')
      .then(r => { setCustomers(r.data.customers || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  return (
    <div>
      <PageTitle>All Borrowers</PageTitle>
      {loading ? <div style={{ color: '#64748b' }}>Loading...</div> : (
        <div style={S.card}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr>
                {['Name', 'Phone', 'Lender', 'Pincode', 'Trust Score', 'GSTIN', 'Loans', 'Active Due', 'Status'].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {customers.map(c => (
                <tr key={c.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <td style={{ ...S.td, fontWeight: '700', color: '#1e293b' }}>{c.full_name || c.name || '—'}</td>
                  <td style={S.td}>{c.phone}</td>
                  <td style={S.td}>{c.admin_shop || c.lender || '—'}</td>
                  <td style={S.td}>{c.current_pincode}</td>
                  <td style={S.td}>
                    <strong style={{ color: c.trust_score >= 80 ? '#4ade80' : c.trust_score >= 50 ? '#fbbf24' : '#f87171' }}>
                      {c.trust_score}
                    </strong>
                  </td>
                  <td style={S.td}>
                    {c.gstin
                      ? <span style={{ fontSize: '11px', fontFamily: 'monospace', background: 'rgba(74,222,128,0.1)', color: '#4ade80', padding: '2px 6px', borderRadius: '4px' }}>{c.gstin}</span>
                      : <span style={{ color: '#475569' }}>—</span>}
                  </td>
                  <td style={{ ...S.td, textAlign: 'center' }}>{c.loan_count || 0}</td>
                  <td style={{ ...S.td, fontWeight: '700', color: parseFloat(c.active_due) > 0 ? '#f87171' : '#64748b' }}>{fmt(c.active_due)}</td>
                  <td style={S.td}>
                    <span style={S.badge(c.status === 'APPROVED' ? 'green' : c.status === 'PENDING' ? 'yellow' : 'red')}>{c.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── REPORTS ──────────────────────────────────────────────────────────────────
function Reports() {
  const [reports, setReports] = useState(null);
  useEffect(() => {
    api.get('/super-admin/reports').then(r => setReports(r.data.reports)).catch(() => toast.error('Failed'));
  }, []);

  if (!reports) return <div style={{ color: '#64748b', padding: '2rem' }}>Loading reports...</div>;

  return (
    <div>
      <PageTitle>Platform Reports</PageTitle>
      <div style={S.card}>
        <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b', margin: '0 0 1rem' }}>Risk Overview</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '1rem' }}>
          <div style={{ ...S.statCard, textAlign: 'center' }}>
            <div style={{ fontSize: '28px', fontWeight: '800', color: '#4ade80' }}>{parseFloat(reports.risk_report?.trustworthy_pct || 0).toFixed(1)}%</div>
            <div style={{ fontSize: '12px', color: '#94a3b8' }}>Trustworthy Customers</div>
          </div>
          <div style={{ ...S.statCard, textAlign: 'center' }}>
            <div style={{ fontSize: '28px', fontWeight: '800', color: '#f87171' }}>{parseFloat(reports.risk_report?.blacklist_pct || 0).toFixed(1)}%</div>
            <div style={{ fontSize: '12px', color: '#94a3b8' }}>Blacklisted</div>
          </div>
          <div style={{ ...S.statCard, textAlign: 'center' }}>
            <div style={{ fontSize: '28px', fontWeight: '800', color: '#c084fc' }}>{reports.risk_report?.defaulters || 0}</div>
            <div style={{ fontSize: '12px', color: '#94a3b8' }}>Total Defaulters</div>
          </div>
        </div>
      </div>

      <div style={{ ...S.card, marginTop: '1.5rem' }}>
        <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b', margin: '0 0 1rem' }}>Top Lenders by Volume</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr>
              {['Shop', 'City', 'Loans', 'Total Lent', 'Recovered'].map(h => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {reports.top_admins?.map((a, i) => (
              <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <td style={{ ...S.td, fontWeight: '700', color: '#1e293b' }}>{a.shop_name || '—'}</td>
                <td style={S.td}>{a.city || '—'}</td>
                <td style={{ ...S.td, textAlign: 'center' }}>{a.loan_count || 0}</td>
                <td style={{ ...S.td, fontWeight: '700', color: '#F97316' }}>{fmt(a.total_lent)}</td>
                <td style={{ ...S.td, color: '#4ade80' }}>{fmt(a.total_recovered)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── MY PROFILE ───────────────────────────────────────────────────────────────
function Profile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm]       = useState({ name: '', phone: '', current_password: '', new_password: '', confirm_password: '' });

  useEffect(() => {
    api.get('/super-admin/profile').then(r => {
      setProfile(r.data.profile);
      setForm(f => ({ ...f, name: r.data.profile.name || r.data.profile.full_name || '', phone: r.data.profile.phone || '' }));
    }).catch(() => toast.error('Failed to load profile'));
  }, []);

  const save = async () => {
    if (form.new_password && form.new_password !== form.confirm_password)
      return toast.error('New passwords do not match');
    if (form.new_password && form.new_password.length < 6)
      return toast.error('Password must be at least 6 characters');
    setLoading(true);
    try {
      await api.put('/super-admin/profile', {
        name: form.name,
        phone: form.phone,
        current_password: form.current_password || undefined,
        new_password: form.new_password || undefined,
      });
      toast.success('Profile updated successfully!');
      setEditing(false);
      setForm(f => ({ ...f, current_password: '', new_password: '', confirm_password: '' }));
      const r = await api.get('/super-admin/profile');
      setProfile(r.data.profile);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    } finally { setLoading(false); }
  };

  if (!profile) return <div style={{ color: '#64748b', padding: '2rem' }}>Loading...</div>;

  return (
    <div>
      <PageTitle>My Profile</PageTitle>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>

        {/* Profile Info */}
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b', margin: 0 }}>Account Information</h3>
            {!editing && <button onClick={() => setEditing(true)} style={S.btn('primary')}>✏️ Edit</button>}
          </div>

          {!editing ? (
            <div>
              {[
                { label: 'Full Name',    value: profile.name || profile.full_name || '—' },
                { label: 'Email',        value: profile.email || '—' },
                { label: 'Phone',        value: profile.phone || '—' },
                { label: 'Role',         value: 'Super Admin / Platform Owner' },
                { label: 'Member Since', value: new Date(profile.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' }) },
              ].map((item, i) => (
                <div key={i} style={{ padding: '12px 0', borderBottom: i < 4 ? '1px solid #f1f5f9' : 'none' }}>
                  <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '3px' }}>{item.label}</div>
                  <div style={{ fontSize: '14px', color: '#1e293b', fontWeight: '500' }}>{item.value}</div>
                </div>
              ))}
            </div>
          ) : (
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>Full Name</label>
              <input style={S.inp} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Your name" />

              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>Phone</label>
              <input style={S.inp} value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="Phone number" maxLength={10} />

              <div style={{ display: 'flex', gap: '8px', marginTop: '1rem' }}>
                <button onClick={() => setEditing(false)} style={{ ...S.btn('outline'), flex: 1 }}>Cancel</button>
                <button onClick={save} disabled={loading} style={{ ...S.btn('primary'), flex: 2 }}>
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Change Password */}
        <div style={S.card}>
          <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b', margin: '0 0 1.5rem' }}>🔒 Change Password</h3>

          <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>Current Password</label>
          <input style={S.inp} type="password" placeholder="Enter current password" value={form.current_password} onChange={e => setForm(f => ({ ...f, current_password: e.target.value }))} />

          <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>New Password</label>
          <input style={S.inp} type="password" placeholder="Min 6 characters" value={form.new_password} onChange={e => setForm(f => ({ ...f, new_password: e.target.value }))} />

          <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>Confirm New Password</label>
          <input style={S.inp} type="password" placeholder="Repeat new password" value={form.confirm_password} onChange={e => setForm(f => ({ ...f, confirm_password: e.target.value }))} />

          <button
            onClick={save}
            disabled={loading || !form.current_password || !form.new_password}
            style={{
              width: '100%', padding: '13px', borderRadius: '12px', border: 'none',
              background: form.current_password && form.new_password
                ? 'linear-gradient(135deg,#F97316,#EF4444)'
                : 'rgba(100,116,139,0.2)',
              color: form.current_password && form.new_password ? '#fff' : '#475569',
              fontWeight: '700', fontSize: '14px',
              cursor: form.current_password && form.new_password ? 'pointer' : 'not-allowed',
              fontFamily: "'Sora', sans-serif",
            }}
          >
            {loading ? 'Updating...' : 'Update Password 🔒'}
          </button>
        </div>
      </div>

      {/* Platform Access */}
      <div style={{ ...S.card, marginTop: '1.5rem' }}>
        <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b', margin: '0 0 1rem' }}>Platform Access</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '1rem' }}>
          {[
            { label: 'Role',           value: 'Super Admin',   icon: '🏛️' },
            { label: 'Access Level',   value: 'Full Platform', icon: '🔑' },
            { label: 'Account Status', value: 'Active',        icon: '✅' },
            { label: 'Email',          value: profile.email,   icon: '📧' },
          ].map((s, i) => (
            <div key={i} style={{ ...S.statCard, textAlign: 'center' }}>
              <div style={{ fontSize: '22px', marginBottom: '6px' }}>{s.icon}</div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#1e293b' }}>{s.value}</div>
              <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── ANALYTICS ────────────────────────────────────────────────────────────────
const PIE_COLORS = { APPROVED: '#22c55e', PENDING: '#f59e0b', REJECTED: '#ef4444', SUSPENDED: '#94a3b8', DEFAULTED: '#dc2626' };
const LOAN_COLORS = { ACTIVE: '#3b82f6', PAID: '#22c55e', OVERDUE: '#f59e0b', DEFAULTED: '#ef4444' };

function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/super-admin/analytics')
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => { toast.error('Failed to load analytics'); setLoading(false); });
  }, []);

  if (loading) return <div style={{ color: '#64748b', padding: '2rem' }}>Loading analytics...</div>;
  if (!data) return null;

  const fmt = (n) => `₹${parseFloat(n || 0).toLocaleString('en-IN')}`;

  return (
    <div>
      <PageTitle>Platform Analytics</PageTitle>

      {/* Monthly Bar Chart */}
      <div style={{ ...S.card, marginBottom: '1.5rem' }}>
        <h3 style={{ margin: '0 0 1.5rem', fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>📊 Monthly Loan Disbursed vs Recovered (Last 6 Months)</h3>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={data.monthly} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#64748b' }} />
            <YAxis tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: '#64748b' }} />
            <Tooltip formatter={(value) => fmt(value)} contentStyle={{ borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '13px' }} />
            <Legend />
            <Bar dataKey="disbursed" name="Disbursed" fill="#3b82f6" radius={[4,4,0,0]} />
            <Bar dataKey="recovered" name="Recovered" fill="#22c55e" radius={[4,4,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
        {/* Customer Status Pie */}
        <div style={S.card}>
          <h3 style={{ margin: '0 0 1rem', fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>👥 Customer Status</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={data.customerStatus} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={80} label={({ status, count }) => `${status}: ${count}`} labelLine={false} fontSize={11}>
                {data.customerStatus.map((entry) => (
                  <Cell key={entry.status} fill={PIE_COLORS[entry.status] || '#94a3b8'} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Loan Status Pie */}
        <div style={S.card}>
          <h3 style={{ margin: '0 0 1rem', fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>💰 Loan Status</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={data.loanStatus} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={80} label={({ status, count }) => `${status}: ${count}`} labelLine={false} fontSize={11}>
                {data.loanStatus.map((entry) => (
                  <Cell key={entry.status} fill={LOAN_COLORS[entry.status] || '#94a3b8'} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top Lenders Table */}
      <div style={S.card}>
        <h3 style={{ margin: '0 0 1rem', fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>🏆 Top Lenders by Volume</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #f1f5f9' }}>
              {['Lender', 'Loans', 'Total Disbursed', 'Total Recovered', 'Collection Rate'].map(h => (
                <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: '#475569', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.topAdmins.map((a, i) => {
              const rate = a.total_disbursed > 0 ? ((a.total_recovered / a.total_disbursed) * 100).toFixed(1) : 0;
              return (
                <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '12px', fontWeight: '600', color: '#1e293b' }}>{a.shop_name}</td>
                  <td style={{ padding: '12px', color: '#64748b' }}>{a.loan_count}</td>
                  <td style={{ padding: '12px', color: '#3b82f6', fontWeight: '600' }}>{fmt(a.total_disbursed)}</td>
                  <td style={{ padding: '12px', color: '#22c55e', fontWeight: '600' }}>{fmt(a.total_recovered)}</td>
                  <td style={{ padding: '12px' }}>
                    <span style={{ background: rate >= 80 ? '#dcfce7' : rate >= 50 ? '#fef9c3' : '#fee2e2', color: rate >= 80 ? '#166534' : rate >= 50 ? '#854d0e' : '#991b1b', padding: '3px 8px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>{rate}%</span>
                  </td>
                </tr>
              );
            })}
            {!data.topAdmins.length && <tr><td colSpan={5} style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>No data yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── NOTIFICATIONS ────────────────────────────────────────────────────────────
function Notifications() {
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/notifications').then(r => { setNotifs(r.data.notifications || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const markRead = async (id) => {
    await api.put(`/notifications/${id}/read`);
    setNotifs(n => n.map(x => x.id === id ? { ...x, is_read: true } : x));
  };

  const markAllRead = async () => {
    await api.put('/notifications/read-all');
    setNotifs(n => n.map(x => ({ ...x, is_read: true })));
    toast.success('All marked as read');
  };

  const deleteOne = async (e, id) => {
    e.stopPropagation();
    await api.delete(`/notifications/${id}`);
    setNotifs(n => n.filter(x => x.id !== id));
  };

  const deleteAll = async () => {
    await api.delete('/notifications');
    setNotifs([]);
    toast.success('All notifications cleared');
  };

  const icons = {
    LOAN_CREATED: '💰', PAYMENT_RECORDED: '✅', LOAN_OVERDUE: '⚠️',
    ACCOUNT_APPROVED: '🎉', ACCOUNT_REJECTED: '❌', NEW_CUSTOMER: '👤',
    NEW_ADMIN_REQUEST: '🏪', UPI_PAYMENT_REQUEST: '📱', PAYMENT_INTIMATION: '💳',
    LOAN_DEFAULTED: '⛔',
  };

  if (loading) return <div style={{ color: '#64748b', padding: '2rem' }}>Loading...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <PageTitle>Platform Activity</PageTitle>
        <div style={{ display: 'flex', gap: '8px' }}>
          {notifs.some(n => !n.is_read) && (
            <button onClick={markAllRead} style={S.btn('outline')}>✓ Mark All Read</button>
          )}
          {notifs.length > 0 && (
            <button onClick={deleteAll} style={{ ...S.btn('outline'), color: '#ef4444', borderColor: '#fecaca' }}>🗑️ Clear All</button>
          )}
        </div>
      </div>
      {!notifs.length ? (
        <div style={{ ...S.card, textAlign: 'center', padding: '3rem' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔔</div>
          <p style={{ color: '#64748b' }}>No activity yet.</p>
        </div>
      ) : notifs.map(n => (
        <div key={n.id} onClick={() => !n.is_read && markRead(n.id)}
          style={{ ...S.card, marginBottom: '10px', background: n.is_read ? '#fff' : '#fff7ed', border: n.is_read ? '1px solid #f1f5f9' : '1px solid #fed7aa', cursor: !n.is_read ? 'pointer' : 'default' }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
            <span style={{ fontSize: '22px' }}>{icons[n.type] || '📢'}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b' }}>{n.title}</div>
              <div style={{ fontSize: '13px', color: '#475569', marginTop: '2px' }}>{n.message}</div>
              <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>{new Date(n.created_at).toLocaleString('en-IN')}</div>
            </div>
            {!n.is_read && <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#F97316', flexShrink: 0, marginTop: '6px' }} />}
            <button onClick={(e) => deleteOne(e, n.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '15px', color: '#94a3b8', padding: '2px 4px', lineHeight: 1, flexShrink: 0 }} title="Delete">🗑️</button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── EXPORT ───────────────────────────────────────────────────────────────────
export default function SuperAdminDashboard() {
  return (
    <Layout>
      <Routes>
        <Route index element={<Overview />} />
        <Route path="admins" element={<Admins />} />
        <Route path="customers" element={<AllCustomers />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="reports" element={<Reports />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="profile" element={<Profile />} />
      </Routes>
    </Layout>
  );
}
